import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useToggle } from '@/hook/useToggle';
import { CloseButton, ModalContainer, Overlay } from './styles';

export const Modal = ({
  children,
  isOpen,
  toggleModal,
  onOpen,
  onClose,
  disableClose,
  closeOnOverlayClick,
  hideCloseButton,
  topOffset,
  maxWidth
}) => {
  const [isClosing, toggleClosing] = useToggle(false);

  const handleClose = useCallback(() => {
    toggleClosing();
    setTimeout(() => {
      toggleClosing();
      toggleModal();
      if (onClose) onClose();
    }, 400);
  }, [toggleModal, onClose, toggleClosing]);

  // `closeOnOverlayClick` es más fino que `disableClose`: apaga sólo el cierre por click afuera y
  // deja la X y el Esc. Sirve para formularios dentro de un modal, donde un click al costado no
  // debería descartar lo que la persona venía cargando.
  const handleOverlayClick = () => {
    if (!disableClose && closeOnOverlayClick) {
      handleClose();
    }
  };

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && !disableClose) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      if (onOpen) onOpen();
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, disableClose, handleClose, onOpen]);

  if (!isOpen && !isClosing) {
    return null;
  }

  const modalContent = (
    <Overlay topOffset={topOffset} onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()} isClosing={isClosing} maxWidth={maxWidth}>
        {!hideCloseButton && !disableClose && (
          <CloseButton onClick={handleClose} aria-label='Close Modal'>
            <span className='hr-Cross' />
          </CloseButton>
        )}
        {children}
      </ModalContainer>
    </Overlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  disableClose: PropTypes.bool,
  closeOnOverlayClick: PropTypes.bool,
  hideCloseButton: PropTypes.bool,
  topOffset: PropTypes.number,
  maxWidth: PropTypes.string
};

Modal.defaultProps = {
  disableClose: false,
  closeOnOverlayClick: true,
  hideCloseButton: false,
  onOpen: null,
  onClose: null,
  topOffset: null,
  maxWidth: null
};
